(ns client-portal.client.anonymous.data
  (:require [client-portal.client.concepts.data :as concepts.d]
            [clojure.string :as s]
            [shared.core.account.data :as account.d]
            [shared.core.components.auth0.data :as auth0.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def local-storage-path [:anonymous-mode?])
(def demo-org-name "SoundHound Demo")
(def demo-logo-url "/images/sh-demo-logo.svg")

(defn enabled? [db]
  (boolean (and (account.d/has-every-role? (account.d/account-user-roles db) [account.d/admin-role])
                (get-in db (into [:local-storage] local-storage-path)))))

(re/reg-sub :anonymous/enabled?- enabled?)

(re/reg-event-fx
  :anonymous/toggle re/middleware
  (fn [{:keys [local-storage]}]
    {:local-storage (update-in local-storage local-storage-path not)}))

(defn- demo-concept-name [concept-id]
  (str "SoundHound " concept-id))

(defn ctx
  "Nil unless anonymous mode is on; otherwise the real brand names (longest first)
   to scrub out of free text. Each name's leading words count too (\"Fuzzys\" for
   \"Fuzzys Taco Shop\") when they're 4+ letters/digits and capitalized in the text,
   so \"Little\" from \"Little Caesars\" leaves \"our little flyers\" alone."
  [db]
  (when (enabled? db)
    (let [concepts-data (get-in db (concepts.d/list-data-path db))
          _ (when (nil? concepts-data)
              (re/dispatch-load [:concepts/load-list-data]))
          with-leading-words (fn [[real demo]]
                               (let [words (some-> real (s/trim) (s/split #"\s+"))]
                                 (->> (range 1 (inc (count words)))
                                      (map #(s/join " " (take % words)))
                                      (filter #(>= (count (s/replace % #"(?i)[^a-z0-9]" "")) 4))
                                      (map #(vector % demo (< (count %) (count real)))))))
          replacements (->> (util/when-not-loading concepts-data)
                            (mapcat (fn [{:keys [id label brand-configuration]}]
                                      [[label (demo-concept-name id)]
                                       [(:display-name brand-configuration) (demo-concept-name id)]]))
                            (cons [(auth0.d/auth0-ns-organization-display-name db) demo-org-name])
                            (filter (comp not-empty first))
                            (mapcat with-leading-words)
                            (remove (fn [[real demo]] (s/includes? (s/lower-case demo) (s/lower-case real))))
                            (distinct)
                            (sort-by (juxt (comp - count first) #(nth % 2))))]
      {:replacements replacements})))

(re/reg-sub :anonymous/ctx- ctx)

(defn- current-ctx []
  @(re/subscribe [:anonymous/ctx-]))

(defn- scrub
  "Swaps each match for a placeholder first, so a demo name already put in is never
   matched again by a shorter brand name (e.g. \"SoundHound\")."
  [{:keys [replacements]} text]
  (let [placeholder #(str "\u0000" % "\u0000")]
    (as-> text t
      (reduce (fn [t [idx [real _ partial?]]]
                (util/replace-all-ignore-case-and-punctuation t real (placeholder idx) {:capitalized? partial?}))
              t
              (map-indexed vector replacements))
      (reduce (fn [t [idx [_ demo _]]]
                (s/replace t (placeholder idx) demo))
              t
              (map-indexed vector replacements)))))

(defn concept-label
  "Display name of a concept (`:brand-configuration :display-name`, else `:label`)."
  ([concept] (concept-label (current-ctx) concept))
  ([anon-ctx {:keys [id label brand-configuration]}]
   (let [real (or (not-empty (:display-name brand-configuration)) label)]
     (cond
       (or (not anon-ctx) (nil? real)) real
       (some? id) (demo-concept-name id)
       :else (scrub anon-ctx real)))))

(defn text
  "Free text that may mention a brand, e.g. an utterance transcript."
  ([t] (text (current-ctx) t))
  ([anon-ctx t]
   (if (and anon-ctx (string? t))
     (scrub anon-ctx t)
     t)))

(defn location-label
  ([label] (location-label (current-ctx) label))
  ([anon-ctx label]
   (text anon-ctx label)))

(defn org-label
  ([org-name] (org-label (current-ctx) org-name))
  ([anon-ctx org-name]
   (if (and anon-ctx org-name)
     demo-org-name
     org-name)))

(defn logo-url
  ([url] (logo-url (current-ctx) url))
  ([anon-ctx url]
   (if (and anon-ctx url)
     demo-logo-url
     url)))
